/*
 * Copyright (c) 2017-2026 Brighter AI Technologies GmbH. All rights reserved.
 * Unauthorized use, copying, reproduction, or distribution of this file, via any medium,
 * is strictly prohibited.
 */

import {
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
  DialogActions,
  DialogContent,
  CircularProgress,
} from "@material-ui/core";
import * as React from "react";
import { useDataProvider, useNotify, useRedirect } from "react-admin";
import { useHistory } from "react-router-dom";
import CloseIcon from "@material-ui/icons/Close";
import { QuestionnaireSelectInput } from "./SelectInput";
import questions from "./questions.json";
import * as gtmUtils from "../../commons/gtmUtils.js";

const useStyles = makeStyles((theme) => ({
  dialog: {
    margin: "auto",
    padding: theme.spacing(4),
  },
  content: {
    height: "100%",
    paddingTop: theme.spacing(5),
  },
  title: {
    width: "80%",
    float: "left",
    fontSize: theme.typography.h3.fontSize,
    paddingLeft: theme.spacing(5),
    paddingTop: theme.spacing(5),
  },
  closeButton: {
    maxWidth: "15%",
    float: "right",
    padding: 0,
  },
  dialogActions: {
    marginTop: theme.spacing(6),
    paddingRight: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  submitButton: {
    width: "20%",
  },
}));

const Questionnaire = (props) => {
  const classes = useStyles();
  const redirect = useRedirect();
  const history = useHistory();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  //  manage state
  const [answersForQuestions, setAnswersForQuestions] = React.useState(
    questions.reduce((acc, question) => {
      return {
        ...acc,
        [question.source]: "",
      };
    }, {})
  );

  const [isLoading, setLoading] = React.useState(false);

  function onClose() {
    redirect("/#npssurvey");
    // refresh page to trigger a page view in hotjar
    // so that hotjar will display the nps survey.
    // add the location hash alone does not trigger a hotjar page view
    history.go(0);
  }

  function onSelect(questionSource, answer) {
    setAnswersForQuestions({
      ...answersForQuestions,
      [questionSource]: answer,
    });
  }

  function submit() {
    setLoading(true);
    dataProvider
      .create("users/questions", { data: answersForQuestions })
      .then((response) => {
        gtmUtils.dispatchGtmEvent("registration-form", "objective questions");
        notify(`Thank you! Your information has been saved.`);
      })
      .catch((error) => {
        //  nothing happening here
      })
      .finally(() => {
        gtmUtils.dispatchGtmEvent(
          "registration-form",
          "account creation successful"
        );
        setLoading(false);
        onClose();
      });
  }

  return (
    <Dialog
      open={props.isOpen}
      keepMounted
      classes={{ paper: classes.dialog }}
      maxWidth={"lg"}
    >
      <DialogTitle>
        <Typography color="secondary" className={classes.title}>
          Please help us cater better for your needs by answering a few
          questions.
        </Typography>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {questions.map((question) => (
          <QuestionnaireSelectInput
            question={question}
            key={`wrapper-${question.source}`}
            onSelect={onSelect}
            answersForQuestions={answersForQuestions}
          />
        ))}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.submitButton}
          variant="contained"
          onClick={submit}
        >
          {isLoading ? (
            <CircularProgress size={25} color="secondary" />
          ) : (
            "Submit"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Questionnaire;
